import type { IntlShape, MessageDescriptor } from 'react-intl';
import type { ArtistProfileKind, ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { profileMessages as m } from '../profileMessages';
import type { ProfileCardModel, ProfileCardTileKey } from './profileCardModel';
import { profileEntryMessages as e } from './profileEntryMessages';

/** The name of a kind inside the sub line ("Make the Facebook cover and press photo …"). */
export const KIND_IN_SENTENCE: Readonly<Record<ArtistProfileKind, MessageDescriptor>> = {
  spotifyHeader: e.kindSpotifyHeader,
  spotifyAvatar: e.kindSpotifyAvatar,
  youtubeBanner: e.kindYoutubeBanner,
  facebookCover: e.kindFacebookCover,
  pressPhoto: e.kindPressPhoto,
};

/** The name and the size of each tile (design p1: "YouTube banner 2560 x 1440"). */
export const TILE_TEXT: Readonly<Record<ProfileCardTileKey, { name: MessageDescriptor; size: MessageDescriptor }>> = {
  spotify: { name: m.spotifyName, size: e.tileSpotifySize },
  youtube: { name: m.youtubeName, size: e.tileYoutubeSize },
  facebook: { name: m.facebookName, size: m.facebookSize },
  press: { name: m.pressName, size: e.tilePressSize },
  bio: { name: m.bioName, size: e.tileBioSize },
};

/** The title and the sub line of the card for its state (design p1–p4, p3 "No release"). */
export function profileCardHeadText(
  intl: IntlShape,
  model: ProfileCardModel,
  newestRelease: ArtistProfileResponse['newestRelease']
): { title: string; sub: string } {
  const list = (kinds: readonly ArtistProfileKind[]) =>
    intl.formatList(
      kinds.map((kind) => intl.formatMessage(KIND_IN_SENTENCE[kind])),
      { type: 'conjunction' }
    );
  switch (model.mode) {
    case 'noSong':
      return { title: intl.formatMessage(m.emptyNoSongTitle), sub: intl.formatMessage(m.emptyNoSongBody) };
    case 'empty':
      return {
        title: intl.formatMessage(m.emptyTitle, { title: newestRelease?.title ?? '' }),
        sub: intl.formatMessage(e.subEmpty),
      };
    case 'making':
      return {
        title: intl.formatMessage(e.titleSome),
        sub: intl.formatMessage(e.subMaking, { count: model.making.length, list: list(model.making) }),
      };
    case 'done':
      return { title: intl.formatMessage(e.titleDone), sub: intl.formatMessage(e.subDone) };
    case 'some':
      return {
        title: intl.formatMessage(e.titleSome),
        sub: intl.formatMessage(e.subSome, { ready: model.ready, total: model.total, list: list(model.notMade) }),
      };
    default: {
      const exhaustive: never = model.mode;
      return exhaustive;
    }
  }
}
